/**
 * usePushNotifications Hook
 *
 * React hook for managing browser push notification subscriptions.
 * Handles permission requests, service worker registration, and server sync.
 *
 * Issue 6.4 - Browser Push Notifications
 *
 * Features:
 * - Checks browser support for push notifications
 * - Manages notification permission state
 * - Registers service worker
 * - Creates and syncs push subscriptions with server
 * - Handles subscription removal
 */

import { useState, useCallback, useEffect } from 'react';
import {
  fetchVapidPublicKey,
  savePushSubscription,
  removePushSubscription,
  fetchPushSubscriptionStatus,
} from '../../../../../api/dispatch/dispatchApi';
import { PushSubscriptionStatus } from '../../../../../api/dispatch/types';

// ============================================
// Types
// ============================================

/** Permission states */
export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

/** Hook return value */
export interface UsePushNotificationsResult {
  /** Whether the browser supports push notifications */
  isSupported: boolean;
  /** Whether push is configured on the server (VAPID keys present) */
  isServerConfigured: boolean;
  /** Current notification permission state */
  permission: PushPermissionState;
  /** Whether the user has an active subscription on this browser */
  isSubscribed: boolean;
  /** Server-side subscription status */
  subscriptionStatus: PushSubscriptionStatus | null;
  /** Whether any async operation is in progress */
  isLoading: boolean;
  /** Error message if something went wrong */
  error: string | null;
  /** Request permission and subscribe */
  subscribe: () => Promise<boolean>;
  /** Unsubscribe from push notifications */
  unsubscribe: () => Promise<boolean>;
  /** Refresh subscription status from server */
  refreshStatus: () => Promise<void>;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Convert base64 URL-safe string to Uint8Array
 * Required for VAPID public key conversion
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

/**
 * Check if push notifications are supported in this browser
 */
function checkPushSupport(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get current notification permission
 */
function getPermissionState(): PushPermissionState {
  if (!checkPushSupport()) return 'unsupported';
  return Notification.permission as PushPermissionState;
}

// ============================================
// Hook
// ============================================

/**
 * Hook to manage browser push notification subscriptions
 *
 * @returns Push notification management functions and state
 *
 * @example
 * const {
 *   isSupported,
 *   permission,
 *   isSubscribed,
 *   subscribe,
 *   unsubscribe
 * } = usePushNotifications();
 *
 * // Request permission and subscribe
 * const handleEnable = async () => {
 *   const success = await subscribe();
 *   if (success) {
 *     showToast('Push notifications enabled!');
 *   }
 * };
 */
export function usePushNotifications(): UsePushNotificationsResult {
  // State
  const [isSupported] = useState(checkPushSupport);
  const [isServerConfigured, setIsServerConfigured] = useState(false);
  const [permission, setPermission] = useState<PushPermissionState>(getPermissionState);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<PushSubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);

  // ----------------------------------------
  // Check server configuration and existing subscription on mount
  // ----------------------------------------
  useEffect(() => {
    const initialize = async () => {
      try {
        // Check if server has VAPID keys configured
        const vapidResponse = await fetchVapidPublicKey();
        setIsServerConfigured(vapidResponse.available);
        if (vapidResponse.publicKey) {
          setVapidPublicKey(vapidResponse.publicKey);
        }

        // Check if we have an existing subscription
        if (isSupported && vapidResponse.available) {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);

          // Also fetch server-side status
          try {
            const status = await fetchPushSubscriptionStatus();
            setSubscriptionStatus(status);
          } catch (e) {
            // Ignore - user might not be authenticated yet
            console.log('[PushNotifications] Could not fetch subscription status:', e);
          }
        }
      } catch (e) {
        console.error('[PushNotifications] Initialization error:', e);
      }
    };

    initialize();
  }, [isSupported]);

  // ----------------------------------------
  // Register service worker
  // ----------------------------------------
  const registerServiceWorker = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    try {
      // Check if already registered
      const existingReg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (existingReg) {
        return existingReg;
      }

      // Register new service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[PushNotifications] Service worker registered:', registration.scope);

      // Wait for it to be ready
      await navigator.serviceWorker.ready;
      return registration;
    } catch (e) {
      console.error('[PushNotifications] Service worker registration failed:', e);
      return null;
    }
  }, []);

  // ----------------------------------------
  // Subscribe to push notifications
  // ----------------------------------------
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    if (!isServerConfigured || !vapidPublicKey) {
      setError('Push notifications are not configured on the server');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult as PushPermissionState);

      if (permissionResult !== 'granted') {
        setError('Notification permission was denied');
        setIsLoading(false);
        return false;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) {
        setError('Failed to register service worker');
        setIsLoading(false);
        return false;
      }

      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      // Send subscription to server
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth')),
        },
      };

      const result = await savePushSubscription(subscriptionData);

      if (!result.success) {
        setError(result.error ?? 'Failed to save subscription');
        setIsLoading(false);
        return false;
      }

      setIsSubscribed(true);
      console.log('[PushNotifications] Subscribed successfully');

      // Refresh status
      try {
        const status = await fetchPushSubscriptionStatus();
        setSubscriptionStatus(status);
      } catch {
        // Ignore
      }

      setIsLoading(false);
      return true;
    } catch (e) {
      console.error('[PushNotifications] Subscribe error:', e);
      setError(e instanceof Error ? e.message : 'Failed to subscribe');
      setIsLoading(false);
      return false;
    }
  }, [isSupported, isServerConfigured, vapidPublicKey, registerServiceWorker]);

  // ----------------------------------------
  // Unsubscribe from push notifications
  // ----------------------------------------
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove from server first
        try {
          await removePushSubscription(subscription.endpoint);
        } catch (e) {
          console.warn('[PushNotifications] Failed to remove from server:', e);
          // Continue anyway - we still want to unsubscribe locally
        }

        // Unsubscribe locally
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      console.log('[PushNotifications] Unsubscribed successfully');

      // Refresh status
      try {
        const status = await fetchPushSubscriptionStatus();
        setSubscriptionStatus(status);
      } catch {
        // Ignore
      }

      setIsLoading(false);
      return true;
    } catch (e) {
      console.error('[PushNotifications] Unsubscribe error:', e);
      setError(e instanceof Error ? e.message : 'Failed to unsubscribe');
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  // ----------------------------------------
  // Refresh subscription status from server
  // ----------------------------------------
  const refreshStatus = useCallback(async (): Promise<void> => {
    try {
      const status = await fetchPushSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (e) {
      console.error('[PushNotifications] Failed to refresh status:', e);
    }
  }, []);

  return {
    isSupported,
    isServerConfigured,
    permission,
    isSubscribed,
    subscriptionStatus,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    refreshStatus,
  };
}

export default usePushNotifications;
